import { useState } from 'react'
import { Notify } from 'notiflix'
import api from '../../../../helpers/apiRequester'
import styles from '../shared/mhlShared.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { MhlLeadRecord } from '../shared/mhlTypes'
import { AUDIOLOGY_LONG_LABELS, audiologyTone, formatDateTime } from '../shared/mhlFormatting'

interface Props {
    lead: MhlLeadRecord
    hasAudiogram: boolean
    canAssess: boolean
    onChanged: () => void
}

const THRESHOLD = 4

export const formatAssessedDb = (v: number | string | null | undefined) => {
    if (v === null || v === undefined || v === '') return null
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    return `${Number.isInteger(n) ? n : n.toFixed(n * 10 === Math.round(n * 10) ? 1 : 2)} dB`
}

/**
 * Reviewer control for reading the audiogram. Unassessed (TBC) leads show the
 * two-way choice plus an optional measured figure; assessed leads show the
 * recorded value with the option to re-assess.
 */
export const AudiologyAssessment = ({ lead, hasAudiogram, canAssess, onChanged }: Props) => {
    const assessed = lead.audiology === '>=4dB' || lead.audiology === '<4dB'
    const assessedDb = formatAssessedDb(lead.audiology_assessed_db)
    const [editing, setEditing] = useState(false)
    const [dbValue, setDbValue] = useState<string>(lead.audiology_assessed_db != null ? String(lead.audiology_assessed_db) : '')
    const [notes, setNotes] = useState(lead.audiology_assessment_notes || '')
    const [busy, setBusy] = useState(false)

    const showForm = canAssess && (!assessed || editing)
    const parsedDb = dbValue.trim() === '' ? null : Number(dbValue)
    const dbValid = parsedDb === null || (Number.isFinite(parsedDb) && parsedDb >= 0 && parsedDb <= 150)
    const derived = parsedDb !== null && dbValid ? (parsedDb >= THRESHOLD ? '>=4dB' : '<4dB') : null

    const submit = async (payload: { assessedDb?: number | null; audiology?: '>=4dB' | '<4dB' | 'tbc' }) => {
        setBusy(true)
        try {
            const res = await api({
                url: '/military-hearing-loss-assess-audiology',
                data: { case_ref: lead.case_ref, notes: notes.trim() || undefined, ...payload },
            })
            if (!res.data?.success) throw new Error(res.data?.message || 'Failed to record assessment')
            Notify.success(payload.audiology === 'tbc' ? 'Assessment cleared' : 'Audiology assessment recorded')
            setEditing(false)
            onChanged()
        } catch (err: any) {
            Notify.failure(err?.response?.data?.message || err?.message || 'Failed to record assessment')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className={styles.assessBox}>
            <div className={styles.assessHeader}>
                <div className={styles.subTitle} style={{ margin: 0 }}>
                    Audiology assessment
                </div>
                {assessed ? (
                    <Pill tone={audiologyTone(lead.audiology)} dot>
                        {assessedDb ? `${assessedDb} NIHL · ${lead.audiology === '>=4dB' ? 'above' : 'below'} ${THRESHOLD} dB` : AUDIOLOGY_LONG_LABELS[lead.audiology!]}
                    </Pill>
                ) : (
                    <Pill tone="warn" dot>
                        Not yet assessed
                    </Pill>
                )}
            </div>

            {assessed && !editing && (
                <div className={styles.assessMeta}>
                    {lead.audiology_assessed_at ? (
                        <>
                            Assessed {formatDateTime(lead.audiology_assessed_at)}
                            {lead.audiology_assessed_by ? ` by ${lead.audiology_assessed_by.replace(/\s*<.*>$/, '')}` : ''}
                            {lead.audiology_assessment_notes ? ` · ${lead.audiology_assessment_notes}` : ''}
                        </>
                    ) : (
                        <>Reported at submission{lead.submitted_via === 'dashboard' ? ' by the agent' : ''}; no measured figure recorded.</>
                    )}
                    {canAssess && (
                        <button type="button" className={`${styles.btn} ${styles.btnSmall} ${styles.btnGhost}`} onClick={() => setEditing(true)} style={{ marginLeft: 'auto' }}>
                            {lead.audiology_assessed_at ? 'Re-assess' : 'Record measured value'}
                        </button>
                    )}
                </div>
            )}

            {!assessed && !canAssess && (
                <p className={styles.hint} style={{ margin: 0 }}>
                    A team leader will read the audiogram and record whether hearing loss is above or below {THRESHOLD} dB.
                </p>
            )}

            {showForm && (
                <div className={styles.assessForm}>
                    {!hasAudiogram && (
                        <div className={styles.infoBanner} style={{ margin: 0 }}>
                            No audiogram is attached yet. You can still record a result, but upload the file above so the assessment is evidenced.
                        </div>
                    )}
                    <div className={styles.assessRow}>
                        <label className={styles.assessField}>
                            <span>Measured NIHL (dB)</span>
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                min={0}
                                max={150}
                                placeholder="e.g. 6.5"
                                className={`${styles.searchInput} ${!dbValid ? styles.inputInvalid : ''}`}
                                value={dbValue}
                                onChange={(e) => setDbValue(e.target.value)}
                                disabled={busy}
                            />
                        </label>
                        <div className={styles.assessDerived}>
                            {derived ? (
                                <>
                                    <span className={styles.factLabel}>Result</span>
                                    <Pill tone={audiologyTone(derived)} dot>
                                        {AUDIOLOGY_LONG_LABELS[derived]}
                                    </Pill>
                                </>
                            ) : (
                                <span className={styles.hint} style={{ margin: 0 }}>
                                    Enter the figure and the {THRESHOLD} dB result is derived, or choose a result directly.
                                </span>
                            )}
                        </div>
                    </div>
                    <label className={styles.assessField}>
                        <span>Notes (optional)</span>
                        <input className={styles.searchInput} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. bilateral, 4 kHz notch" disabled={busy} />
                    </label>
                    <div className={styles.assessActions}>
                        {derived ? (
                            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !dbValid} onClick={() => submit({ assessedDb: parsedDb })}>
                                {busy ? 'Saving…' : `Record ${formatAssessedDb(parsedDb)} (${derived === '>=4dB' ? 'above' : 'below'} ${THRESHOLD} dB)`}
                            </button>
                        ) : (
                            <>
                                <button type="button" className={`${styles.btn} ${styles.btnSuccess}`} disabled={busy || !dbValid} onClick={() => submit({ audiology: '>=4dB' })}>
                                    Above {THRESHOLD} dB
                                </button>
                                <button type="button" className={`${styles.btn} ${styles.btnDanger}`} disabled={busy || !dbValid} onClick={() => submit({ audiology: '<4dB' })}>
                                    Below {THRESHOLD} dB
                                </button>
                            </>
                        )}
                        {assessed && (
                            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} disabled={busy} onClick={() => submit({ audiology: 'tbc' })} title="Clear the result and return this client to unassessed">
                                Mark as TBC
                            </button>
                        )}
                        {editing && (
                            <button type="button" className={styles.btn} disabled={busy} onClick={() => setEditing(false)} style={{ marginLeft: 'auto' }}>
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
